/**
 * OTO STUDENTS / NASHGO — Passenger Manifest Print Utility
 * توليد وطباعة كشف ركاب الرحلة والمنافيست الرسمي للسائق ومشرف الرحلة
 */

import { Trip } from '../types';
import { TripPassengerEntry } from '../components/admin/AdminTrips';

export interface PrintManifestOptions {
  mode?: 'AUTO' | 'BOOKED_ONLY' | 'ALL_SEATS';
}

export function generateManifestHtml(
  trip: Trip,
  passengers: TripPassengerEntry[],
  options: PrintManifestOptions = {}
): string {
  const totalSeats = trip.totalSeats || trip.capacity || passengers.length || 50;

  // Identify booked passengers
  const isPassengerBooked = (p: TripPassengerEntry): boolean => {
    if (!p) return false;
    if (p.booking) return true;
    if (p.studentName && p.studentName.trim() && !p.studentName.startsWith('مقعد متاح')) return true;
    if (p.ticketId && p.ticketId !== '—' && p.ticketId.trim() !== '') return true;
    if (p.studentPhone && p.studentPhone !== '—' && p.studentPhone.trim() !== '') return true;
    return false;
  };

  const bookedList = passengers.filter(isPassengerBooked);
  const bookedCount = bookedList.length;
  const availableCount = Math.max(0, totalSeats - bookedCount);
  const occupancyRate = Math.round((bookedCount / totalSeats) * 100);

  // Determine mode
  const mode = options.mode || 'AUTO';
  const showAllSeats = mode === 'ALL_SEATS' || (mode === 'AUTO' && bookedCount === 0);

  // Target passengers to display in main table
  const displayPassengers = showAllSeats ? passengers : bookedList;

  // Unbooked seat numbers
  const emptySeatsList = passengers
    .filter((p) => !isPassengerBooked(p))
    .map((p) => p.seatNumber)
    .sort((a, b) => a - b);

  // Format current date and time in Arabic
  const now = new Date();
  const printTimestamp = now.toLocaleDateString('ar-EG', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }) + ' — ' + now.toLocaleTimeString('ar-EG', { hour: '2-digit', minute: '2-digit' });

  // Direction label
  const directionText = trip.direction === 'RETURN' ? 'عودة للإسكندرية' : 'ذهاب للجامعة';

  return `<!DOCTYPE html>
<html dir="rtl" lang="ar">
<head>
  <meta charset="UTF-8">
  <title>كشف_منافيست_رحلة_${trip.tripCode}_${trip.date}</title>
  <style>
    @page {
      size: A4 portrait;
      margin: 8mm 10mm;
    }

    * {
      box-sizing: border-box;
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
    }

    body {
      font-family: 'Cairo', 'Segoe UI', Tahoma, Arial, sans-serif;
      margin: 0;
      padding: 0;
      color: #0f172a;
      background: #ffffff;
      font-size: 11px;
      line-height: 1.35;
      direction: rtl;
    }

    .manifest-wrapper {
      width: 100%;
      max-width: 100%;
      margin: 0 auto;
    }

    /* Header Bar */
    .manifest-topbar {
      display: flex;
      justify-content: space-between;
      align-items: center;
      border-bottom: 2px solid #0f172a;
      padding-bottom: 8px;
      margin-bottom: 10px;
    }

    .manifest-brand {
      display: flex;
      align-items: center;
      gap: 10px;
    }

    .manifest-logo-badge {
      background: #E5232A;
      color: #ffffff;
      font-weight: 900;
      font-size: 15px;
      padding: 4px 10px;
      border-radius: 6px;
      letter-spacing: 0.5px;
      display: inline-block;
    }

    .manifest-title-area h1 {
      margin: 0;
      font-size: 16px;
      font-weight: 800;
      color: #0f172a;
    }

    .manifest-title-area p {
      margin: 2px 0 0 0;
      font-size: 10px;
      color: #475569;
    }

    .manifest-meta-badge {
      text-align: left;
      font-size: 9px;
      color: #334155;
    }

    .manifest-meta-badge .trip-code-pill {
      display: inline-block;
      background: #f1f5f9;
      border: 1px solid #cbd5e1;
      padding: 3px 8px;
      border-radius: 5px;
      font-family: monospace;
      font-weight: 700;
      font-size: 12px;
      color: #0f172a;
      margin-bottom: 3px;
    }

    /* Trip Information Card */
    .trip-info-card {
      background: #f8fafc;
      border: 1px solid #cbd5e1;
      border-radius: 8px;
      padding: 8px 12px;
      margin-bottom: 10px;
      display: grid;
      grid-template-columns: repeat(4, 1fr);
      gap: 8px;
      font-size: 10.5px;
    }

    .info-item {
      display: flex;
      flex-direction: column;
    }

    .info-label {
      color: #64748b;
      font-size: 9.5px;
      margin-bottom: 1px;
    }

    .info-value {
      font-weight: 700;
      color: #0f172a;
    }

    .info-value.dir-badge {
      display: inline-block;
      padding: 2px 6px;
      border-radius: 4px;
      font-size: 9.5px;
      width: fit-content;
      background: ${trip.direction === 'RETURN' ? '#fef3c7' : '#e0e7ff'};
      color: ${trip.direction === 'RETURN' ? '#92400e' : '#3730a3'};
      border: 1px solid ${trip.direction === 'RETURN' ? '#fcd34d' : '#c7d2fe'};
    }

    /* Capacity & Seats Highlight Bar */
    .capacity-summary-strip {
      display: grid;
      grid-template-columns: repeat(4, 1fr);
      gap: 8px;
      margin-bottom: 10px;
    }

    .cap-box {
      border: 1px solid #cbd5e1;
      border-radius: 6px;
      padding: 6px 10px;
      text-align: center;
      background: #ffffff;
    }

    .cap-box.total {
      background: #f8fafc;
      border-color: #94a3b8;
    }

    .cap-box.booked {
      background: #eff6ff;
      border-color: #93c5fd;
      color: #1e40af;
    }

    .cap-box.available {
      background: #fefce8;
      border-color: #fde047;
      color: #854d0e;
    }

    .cap-box.percent {
      background: #f0fdf4;
      border-color: #86efac;
      color: #166534;
    }

    .cap-box .num {
      display: block;
      font-size: 16px;
      font-weight: 900;
      font-family: monospace;
      line-height: 1.1;
    }

    .cap-box .lbl {
      display: block;
      font-size: 9.5px;
      font-weight: 700;
      margin-top: 1px;
    }

    /* Roster Table */
    .roster-table {
      width: 100%;
      border-collapse: collapse;
      margin-bottom: 10px;
      font-size: 10.5px;
    }

    .roster-table th {
      background: #1e293b;
      color: #ffffff;
      padding: 6px 8px;
      text-align: right;
      font-weight: 700;
      font-size: 10px;
      border: 1px solid #1e293b;
      white-space: nowrap;
    }

    .roster-table td {
      border: 1px solid #cbd5e1;
      padding: 5px 8px;
      text-align: right;
      vertical-align: middle;
    }

    .roster-table tbody tr:nth-child(even) {
      background-color: #f8fafc;
    }

    .seat-badge {
      display: inline-block;
      font-family: monospace;
      font-weight: 800;
      font-size: 11px;
      padding: 2px 6px;
      border-radius: 4px;
      background: #f1f5f9;
      border: 1px solid #cbd5e1;
      color: #0f172a;
      min-width: 32px;
      text-align: center;
    }

    .seat-badge.booked {
      background: #fee2e2;
      border-color: #fca5a5;
      color: #991b1b;
    }

    .passenger-name {
      font-weight: 700;
      color: #0f172a;
    }

    .passenger-phone {
      font-family: monospace;
      font-weight: 600;
      color: #1e293b;
      direction: ltr;
      display: inline-block;
    }

    .ticket-code {
      font-family: monospace;
      font-weight: 700;
      color: #0f172a;
      background: #f1f5f9;
      padding: 1px 5px;
      border-radius: 3px;
      border: 1px solid #e2e8f0;
      display: inline-block;
      font-size: 10px;
    }

    .booking-ref {
      font-family: monospace;
      font-size: 9px;
      color: #64748b;
      display: block;
    }

    .empty-row-text {
      color: #94a3b8;
      font-style: italic;
    }

    .check-box-cell {
      text-align: center;
      width: 70px;
    }

    .driver-check-square {
      width: 14px;
      height: 14px;
      border: 1.5px solid #64748b;
      border-radius: 3px;
      display: inline-block;
      vertical-align: middle;
      margin-left: 4px;
    }

    /* Available Seats Bar */
    .available-seats-section {
      background: #fffbeb;
      border: 1px dashed #ca8a04;
      border-radius: 6px;
      padding: 6px 10px;
      margin-bottom: 10px;
      font-size: 10px;
      color: #713f12;
    }

    .available-seats-list {
      display: flex;
      flex-wrap: wrap;
      gap: 4px;
      margin-top: 4px;
    }

    .avail-seat-tag {
      background: #ffffff;
      border: 1px solid #fde047;
      padding: 1px 5px;
      border-radius: 3px;
      font-family: monospace;
      font-weight: 700;
      font-size: 9.5px;
    }

    /* Manual Entry Table for Extra Walk-in Riders */
    .manual-table {
      width: 100%;
      border-collapse: collapse;
      margin-bottom: 12px;
      font-size: 10px;
    }

    .manual-table th {
      background: #475569;
      color: #ffffff;
      padding: 4px 8px;
      border: 1px solid #475569;
      font-size: 9.5px;
    }

    .manual-table td {
      border: 1px solid #cbd5e1;
      padding: 6px 8px;
      height: 22px;
    }

    /* Footer & Signatures */
    .manifest-footer {
      border-top: 1.5px solid #0f172a;
      padding-top: 8px;
      margin-top: 10px;
      page-break-inside: avoid;
    }

    .undertaking-text {
      font-size: 9.5px;
      color: #334155;
      margin-bottom: 8px;
      background: #f8fafc;
      padding: 6px 8px;
      border-radius: 4px;
      border: 1px solid #e2e8f0;
    }

    .signatures-row {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 15px;
      margin-top: 10px;
    }

    .sig-box {
      border: 1px solid #cbd5e1;
      border-radius: 6px;
      padding: 8px;
      min-height: 55px;
      position: relative;
    }

    .sig-title {
      font-weight: 700;
      font-size: 10px;
      color: #0f172a;
      margin-bottom: 4px;
      display: block;
    }

    .sig-line {
      border-bottom: 1px dotted #94a3b8;
      height: 25px;
    }

    .sig-stamp-circle {
      position: absolute;
      left: 10px;
      top: 12px;
      width: 44px;
      height: 44px;
      border: 1px dashed #cbd5e1;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 8px;
      color: #94a3b8;
      text-align: center;
    }

    /* Repeating header for print */
    thead {
      display: table-header-group;
    }

    tr {
      page-break-inside: avoid;
    }
  </style>
</head>
<body>
  <div class="manifest-wrapper">
    <!-- Top Bar with Branding & Code -->
    <div class="manifest-topbar">
      <div class="manifest-brand">
        <span class="manifest-logo-badge">OTO STUDENTS</span>
        <div class="manifest-title-area">
          <h1>كشف ركاب الرحلة والمنافيست الرسمي (Trip Manifest)</h1>
          <p>منظومة نقل طلاب الجامعات من الإسكندرية • إدارة الحركة والتشغيل</p>
        </div>
      </div>
      <div class="manifest-meta-badge">
        <div class="trip-code-pill">${trip.tripCode}</div>
        <div>تاريخ الطباعة: <strong>${printTimestamp}</strong></div>
      </div>
    </div>

    <!-- Trip Details Card -->
    <div class="trip-info-card">
      <div class="info-item">
        <span class="info-label">الجامعة الوجهة:</span>
        <strong class="info-value" style="font-size: 12px;">${trip.universityName}</strong>
      </div>
      <div class="info-item">
        <span class="info-label">تاريخ ووقت التحرك:</span>
        <strong class="info-value">${trip.date} (${trip.departureTime})</strong>
      </div>
      <div class="info-item">
        <span class="info-label">الحافلة والأسطول:</span>
        <strong class="info-value">حافلة سياحية (${totalSeats} راكباً) — ${trip.vehiclePlate || 'س ق د 1842'}</strong>
      </div>
      <div class="info-item">
        <span class="info-label">اتجاه ومسار الرحلة:</span>
        <span class="info-value dir-badge">${directionText}</span>
      </div>

      <div class="info-item">
        <span class="info-label">محطة الانطلاق والتجمع:</span>
        <strong class="info-value">${trip.launchStation || trip.origin || 'محطة مصر / سيدي جابر'}</strong>
      </div>
      <div class="info-item">
        <span class="info-label">وقت الوصول المتوقع:</span>
        <strong class="info-value">${trip.expectedArrivalTime || trip.arrivalTime || '—'}</strong>
      </div>
      <div class="info-item">
        <span class="info-label">كابتن الحافلة (السائق):</span>
        <strong class="info-value">${trip.driverName || 'كابتن الرحلة'}</strong>
      </div>
      <div class="info-item">
        <span class="info-label">هاتف السائق للتواصل:</span>
        <strong class="info-value" style="direction: ltr; font-family: monospace;">${trip.driverPhone || '—'}</strong>
      </div>
    </div>

    <!-- Capacity & Seat Statistics Cards -->
    <div class="capacity-summary-strip">
      <div class="cap-box total">
        <span class="num">${totalSeats}</span>
        <span class="lbl">إجمالي سعة المقاعد</span>
      </div>
      <div class="cap-box booked">
        <span class="num">${bookedCount}</span>
        <span class="lbl">عدد الركاب المحجوزين</span>
      </div>
      <div class="cap-box available">
        <span class="num">${availableCount}</span>
        <span class="lbl">المقاعد المتاحة (الشاغرة)</span>
      </div>
      <div class="cap-box percent">
        <span class="num">${occupancyRate}%</span>
        <span class="lbl">نسبة إشغال الحافلة</span>
      </div>
    </div>

    <!-- Main Manifest Passenger Table -->
    <table class="roster-table">
      <thead>
        <tr>
          <th style="width: 45px; text-align: center;">المقعد</th>
          <th style="width: 170px;">اسم الراكب (الطالب)</th>
          <th style="width: 110px;">رقم الهاتف</th>
          <th style="width: 110px;">رقم التذكرة</th>
          <th style="width: 90px;">كود الحجز</th>
          <th>محطة الركوب</th>
          <th style="width: 80px; text-align: center;">حالة السداد</th>
          <th class="check-box-cell">تحضير / صعود</th>
        </tr>
      </thead>
      <tbody>
        ${
          displayPassengers.length === 0
            ? `
          <tr>
            <td colspan="8" style="text-align: center; padding: 25px; color: #64748b;">
              لا توجد حجوزات مسجلة على هذه الرحلة حتى الآن (كافة المقاعد الـ ${totalSeats} شاغرة).
            </td>
          </tr>
        `
            : displayPassengers
                .map((p) => {
                  const isBooked = isPassengerBooked(p);
                  const cleanPhone = (p.studentPhone || '').replace(/[^0-9]/g, '');
                  const formattedPhone = cleanPhone || p.studentPhone || '—';
                  const ticketNumber = p.ticketId || p.booking?.ticketId || (p.bookingCode ? `TCK-${p.seatNumber}` : '—');
                  const bookingCode = p.bookingCode || p.booking?.bookingCode || '—';
                  const station = p.pickupStation || trip.launchStation || 'المحطة المحددة';
                  const isPaid = p.paymentStatus === 'PAID';

                  return `
            <tr>
              <td style="text-align: center;">
                <span class="seat-badge ${isBooked ? 'booked' : ''}">#${p.seatNumber}</span>
              </td>
              <td>
                ${
                  isBooked
                    ? `<strong class="passenger-name">${p.studentName}</strong>`
                    : `<span class="empty-row-text">مقعد شاغر — متاح للحجز</span>`
                }
              </td>
              <td style="direction: ltr; text-align: right;">
                ${
                  isBooked && formattedPhone !== '—'
                    ? `<span class="passenger-phone">${formattedPhone}</span>`
                    : `<span style="color: #cbd5e1;">—</span>`
                }
              </td>
              <td>
                ${
                  isBooked && ticketNumber !== '—'
                    ? `<span class="ticket-code">${ticketNumber}</span>`
                    : `<span style="color: #cbd5e1;">—</span>`
                }
              </td>
              <td>
                ${
                  isBooked && bookingCode !== '—'
                    ? `<span class="booking-ref">${bookingCode}</span>`
                    : `<span style="color: #cbd5e1;">—</span>`
                }
              </td>
              <td>
                ${isBooked ? station : `<span style="color: #cbd5e1;">—</span>`}
              </td>
              <td style="text-align: center;">
                ${
                  isBooked
                    ? isPaid
                      ? `<span style="color: #166534; font-weight: 700; font-size: 9.5px;">تم السداد ✓</span>`
                      : `<span style="color: #854d0e; font-weight: 700; font-size: 9.5px;">نقداً / معلق</span>`
                    : `<span style="color: #cbd5e1;">—</span>`
                }
              </td>
              <td class="check-box-cell">
                <span class="driver-check-square"></span>
                <span style="font-size: 9px; color: #475569;">${p.attendance === 'BOARDED' ? 'صعد ✓' : 'صعد'}</span>
              </td>
            </tr>
          `;
                })
                .join('')
        }
      </tbody>
    </table>

    ${
      !showAllSeats && emptySeatsList.length > 0
        ? `
      <!-- Available Seats Summary & Manual Entry for Walk-ins -->
      <div class="available-seats-section">
        <strong>🟡 المقاعد الشاغرة المتبقية بالحافلة (${emptySeatsList.length} مقعداً):</strong>
        <div class="available-seats-list">
          ${emptySeatsList.map((num) => `<span class="avail-seat-tag">#${num}</span>`).join('')}
        </div>
      </div>

      <!-- Emergency Walk-in Passengers Registration -->
      <table class="manual-table">
        <thead>
          <tr>
            <th style="width: 45px; text-align: center;">المقعد</th>
            <th>اسم الراكب الإضافي / الطوارئ</th>
            <th style="width: 120px;">رقم الهاتف</th>
            <th style="width: 110px;">رقم الإيصال / التذكرة</th>
            <th style="width: 110px;">محطة الركوب</th>
            <th style="width: 80px; text-align: center;">المبلغ المحصل</th>
            <th style="width: 70px; text-align: center;">توقيع الطالب</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td style="text-align: center; font-weight: bold; color: #64748b;">#</td>
            <td></td>
            <td></td>
            <td></td>
            <td></td>
            <td></td>
            <td></td>
          </tr>
          <tr>
            <td style="text-align: center; font-weight: bold; color: #64748b;">#</td>
            <td></td>
            <td></td>
            <td></td>
            <td></td>
            <td></td>
            <td></td>
          </tr>
          <tr>
            <td style="text-align: center; font-weight: bold; color: #64748b;">#</td>
            <td></td>
            <td></td>
            <td></td>
            <td></td>
            <td></td>
            <td></td>
          </tr>
        </tbody>
      </table>
    `
        : ''
    }

    <!-- Undertaking & Signatures -->
    <div class="manifest-footer">
      <div class="undertaking-text">
        <strong>إقرار السائق:</strong> أقر أنا كابتن الحافلة باستلام كشف الركاب أعلاه ومطابقة هويات وتذاكر الطلاب الصاعدين والالتزام بمسار الرحلة ومحطات التوقف المحددة، وعدم السماح بصعود أي راكب غير مسجل أو وقوف طلاب بالممر نهائياً.
      </div>

      <div class="signatures-row">
        <div class="sig-box">
          <span class="sig-title">توقيع كابتن الحافلة (السائق):</span>
          <div class="sig-line"></div>
        </div>
        <div class="sig-box">
          <span class="sig-title">توقيع مشرف الحركة والمحطة:</span>
          <div class="sig-line"></div>
        </div>
        <div class="sig-box">
          <span class="sig-title">اعتماد وختم إدارة التشغيل:</span>
          <div class="sig-stamp-circle">ختم التشغيل</div>
        </div>
      </div>
    </div>
  </div>
</body>
</html>`;
}

/**
 * Triggers the browser print dialog for the manifest in an isolated, high-fidelity iframe.
 */
export function printTripManifest(
  trip: Trip,
  passengers: TripPassengerEntry[],
  options: PrintManifestOptions = {}
): void {
  const html = generateManifestHtml(trip, passengers, options);

  // Hidden print iframe
  const iframe = document.createElement('iframe');
  iframe.style.position = 'fixed';
  iframe.style.right = '0';
  iframe.style.bottom = '0';
  iframe.style.width = '0';
  iframe.style.height = '0';
  iframe.style.border = '0';
  iframe.style.opacity = '0';
  iframe.setAttribute('title', 'Passenger Manifest Document');

  document.body.appendChild(iframe);

  const iframeDoc = iframe.contentWindow?.document;
  if (!iframeDoc) {
    // Fallback if iframe contentWindow is blocked
    const printWin = window.open('', '_blank');
    if (printWin) {
      printWin.document.write(html);
      printWin.document.close();
      printWin.focus();
      printWin.print();
    }
    return;
  }

  iframeDoc.open();
  iframeDoc.write(html);
  iframeDoc.close();

  // Allow styles and font rendering before invoking print
  setTimeout(() => {
    try {
      iframe.contentWindow?.focus();
      iframe.contentWindow?.print();
    } catch (err) {
      console.warn('Iframe print error, falling back to popup window:', err);
      const printWin = window.open('', '_blank');
      if (printWin) {
        printWin.document.write(html);
        printWin.document.close();
        printWin.focus();
        printWin.print();
      }
    } finally {
      // Clean up iframe after print dialog is closed
      setTimeout(() => {
        try {
          if (iframe.parentNode) {
            iframe.parentNode.removeChild(iframe);
          }
        } catch (_) {}
      }, 3000);
    }
  }, 250);
}
